import { FC, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  doGetSecondItems,
  doCreateSecondItem,
  doUpdateSecondItem,
} from '../../../store/criteria.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import qs from 'qs';

import { useSnackbar } from 'notistack';
import { Button as AntButton, Modal, Table, TableColumnsType } from 'antd';


import Loader from '../../../components/Loader';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Button from '../../../components/Button';
import Radio from '../../../components/Radio';
import {
  successVariant,
  infoVariant,
  warningVariant,
  errorVariant,
} from '../../../lib/barVariant';
import useAuth from '../../../hooks/useAuth';

interface IMatch {
  id: string;
}

const ProgramCriteriaSub: FC<RouteComponentProps> = ({
  history,
  location,
}) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const match = useRouteMatch<IMatch>();
  const parentCode = Number(match.params.id);
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });

  const { secondItems, isLoading } = useSelector(
    (state: RootState) => state.criteria,
  );

  // 상위 항목의 종류 및 이름
  const [type, setType] = useState('');
  const [name, setName] = useState('');

  const [inputVisible, setInputVisible] = useState(false);
  const [itemCode, setItemCode] = useState<number | null>(null);
  const [itemName, setItemName] = useState('');
  const [memo, setMemo] = useState('');
  const [useYn, setUseYn] = useState('Y');

  const itemNameInput = useRef<any>();

  useEffect(() => {
    // query to state
    if (query.type) setType(String(query.type));
    if (query.name) setName(String(query.name));
  }, [location]);

  useEffect(() => {
    getSecondItems();
  }, []);

  const getSecondItems = async () => {
    const res = await dispatch(doGetSecondItems(parentCode));

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(
          res?.data?.data || t('common.message.error'),
          errorVariant,
        );
    }
  };

  const onOpenInputModal = () => {
    setItemCode(null);
    setItemName('');
    setMemo('');
    setUseYn('Y');
    setInputVisible(true);
  };

  const onSubmit = async () => {
    if (!itemName) {
      itemNameInput.current.focus();
      return enqueueSnackbar(t('program.criteria.message1'), infoVariant);
    }

    const req = {
      itemName,
      memo,
      useYn,
    };

    const res = itemCode
      ? await dispatch(doUpdateSecondItem(itemCode, req))
      : await dispatch(doCreateSecondItem(parentCode, req));

    switch (res?.status) {
      case 200:
        getSecondItems();
        setInputVisible(false);
        enqueueSnackbar(t('common.message.save1'), successVariant);
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(
          res?.data?.data || t('common.message.error'),
          errorVariant,
        );
    }
  };

  const columns: TableColumnsType<any> = [
    {
      title: t('program.criteria.column1'),
      dataIndex: 'itemName',
      key: 'itemName',
      align: 'center',
      width: '25%',
      sorter: (a, b) => a.itemName?.localeCompare(b.itemName),
    },
    {
      title: t('program.criteria.column5'),
      dataIndex: 'memo',
      key: 'memo',
      align: 'center',
      sorter: (a, b) => a.memo?.localeCompare(b.memo),
    },
    {
      title: t('program.criteria.column4'),
      dataIndex: 'useYn',
      key: 'useYn',
      align: 'center',
      width: '100px',
      sorter: (a, b) => a.useYn?.localeCompare(b.useYn),
    },
    {
      title: '',
      key: 'itemCode',
      dataIndex: 'itemCode',
      align: 'center',
      width: '100px',
      render: (value: string, record: any) => (
        <>
          <Button
            className="btn btn-sm btn-success btn-w100p"
            onClickCapture={(event) => {
              event.stopPropagation();
              setItemCode(record.itemCode || '');
              setItemName(record.itemName || '');
              setMemo(record.memo || '');
              setUseYn(record.useYn || '');
              setInputVisible(true);
            }}
            text={t('common.button.edit')}
            writeAuthYn={writeAuthYn}
          />
        </>
      ),
    },
  ];

  return (
    <div className="content-wrapper">
      {isLoading ? <Loader /> : null}
      <Header parentMenu={t('common.menu.program')} currentMenu={`${t('program.criteria.title1')} > ${name}`} />
      <div className="main flex">
        <div className="col-6">
          <div>
            <div className="text-right">
              <Button
                className="btn btn-sm btn-primary btn-w100p ml-1"
                onClick={onOpenInputModal}
                text={t('program.award.button2')}
                writeAuthYn={writeAuthYn}
              />
            </div>
            <div className="card mt-3">
              <div className="card-body">
                <Table
                  columns={columns}
                  dataSource={secondItems || undefined}
                  pagination={{
                    pageSize: 99999,
                  }}
                  onRow={(record) => {
                    return {
                      onClick: () => {
                        history.push(
                          `/program/criteria/${record.itemCode}/activity?type=${type}&code=${parentCode}&topName=${name}&subName=${record.itemName}`,
                        );
                      },
                    };
                  }}
                  rowClassName="pointer"
                />
              </div>
            </div>
            <div className="flex flex-justify-between">
              <Button
                className="btn btn-sm btn-info btn-w100p"
                onClick={() => history.push(`/program/criteria?type=${type}`)}
                text={t('program.convert.button1')}
              />
              <Button
                className="btn btn-sm btn-primary btn-w100p"
                onClick={onOpenInputModal}
                text={t('program.award.button2')}
                writeAuthYn={writeAuthYn}
              />
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* 입력 모달 */}
      <Modal
        className="select-agent-modal"
        title={`${t('program.criteria.title1')} > ${name}`}
        visible={inputVisible}
        onCancel={() => setInputVisible(false)}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton key="submit" className='btn-basic pink' onClick={onSubmit}>
              {t('common.button.save')}
            </AntButton>
            <AntButton key="cancel" className='btn-basic gray' onClick={() => setInputVisible(false)}>
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <div>
          <table className="table table-inside">
            <colgroup>
              <col width="20%" />
              <col width="80%" />
            </colgroup>
            <tbody>
              <tr>
                <th className="align-middle">{t('program.criteria.column1')}</th>
                <td>
                  <input
                    type="text"
                    className="form-control input-text"
                    value={itemName}
                    onChange={(e) => setItemName(e.target.value)}
                    ref={itemNameInput}
                  />
                </td>
              </tr>
              <tr>
                <th className="align-middle">{t('program.criteria.column5')}</th>
                <td>
                  <input
                    type="text"
                    className="form-control input-text"
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <th className="align-middle">{t('program.criteria.column4')}</th>
                <td>
                  <Radio
                    checked={useYn === 'Y'}
                    onChange={() => setUseYn('Y')}
                    text={t('common.option.yes')}
                  />
                  <Radio
                    className="ml-4"
                    checked={useYn === 'N'}
                    onChange={() => setUseYn('N')}
                    text={t('common.option.no')}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
};

export default ProgramCriteriaSub;
